import React, { useMemo, useRef, useCallback } from 'react';

import { DiffMetadataProps } from './diff-set';
import {
  FetchDiffHookOptions,
  useDiff,
  useDiffStat,
} from './hooks/diff-loader';

type ContextType = {
  isLoadingDiff: boolean;
  isLoadingDiffStat: boolean;
  loadDiff: () => void;
  loadFileDiff: (fromPath: string, toPath: string) => void;
  loadDiffStat: () => void;
  diffMetadataProps: DiffMetadataProps;
};

export const DiffContext = React.createContext<ContextType>({
  isLoadingDiff: false,
  isLoadingDiffStat: false,
  loadDiff: () => {},
  loadFileDiff: () => {},
  loadDiffStat: () => {},
  diffMetadataProps: {
    sourceHash: '',
    sourceRepoFullSlug: '',
  },
});

export type DiffProviderProps = {
  diffUrlBuilder: (
    shouldIgnoreWhitespace?: boolean,
    topicDiff?: boolean,
    useForkMergebase?: boolean
  ) => string;
  diffStatUrlBuilder: (
    shouldIgnoreWhitespace?: boolean,
    useForkMergebase?: boolean
  ) => string;
  diffMetadataProps: DiffMetadataProps;
  useForkMergebase?: boolean;
  singleHashDiff?: boolean;
  maxDiffStatPages?: number;
  diffFetchOptions?: FetchDiffHookOptions;
};

/**
 * This provider exsits for 2 reasons:
 *
 * 1. Take advantage of the dependency list of useCallback; when rendering a
 *    DiffLoader (even if it had been unmounted), if the dependencies are the
 *    same we shouldn't have to fetch the diff again.
 *
 * 2. Allow for some pages to load the diff at different points. On the pull
 *    request page, we want to pre-load the diff stat before viewing the actual
 *    diff; this allows us to do that without duplicating the call when the diff
 *    is actually rendered.
 */
/* eslint @typescript-eslint/ban-types: "warn" */
export const DiffProvider: React.FC<DiffProviderProps> = props => {
  const {
    diffUrlBuilder,
    diffStatUrlBuilder,
    diffMetadataProps,
    useForkMergebase,
    singleHashDiff,
    maxDiffStatPages,
    diffFetchOptions = {},
  } = props;

  const {
    loadDiff: requestDiff,
    isLoading: isLoadingDiff,
    loadFileDiff,
  } = useDiff(
    diffUrlBuilder,
    diffFetchOptions,
    diffMetadataProps.sourceHash,
    diffMetadataProps.destinationHash,
    useForkMergebase,
    singleHashDiff
  );

  const { loadDiffStat: requestDiffStat, isLoading: isLoadingDiffStat } =
    useDiffStat(
      diffStatUrlBuilder,
      diffMetadataProps.sourceHash,
      diffMetadataProps.destinationHash,
      useForkMergebase,
      singleHashDiff,
      maxDiffStatPages
    );
  const loadDiffRef = useRef(() => {});
  const loadDiffStatRef = useRef(() => {});

  // Only load the diff or diff stat once until we get a new load function
  const loadDiff = useCallback(() => {
    if (loadDiffRef.current === requestDiff) return;
    loadDiffRef.current = requestDiff;
    requestDiff();
  }, [requestDiff]);

  const loadDiffStat = useCallback(() => {
    if (loadDiffStatRef.current === requestDiffStat) return;
    loadDiffStatRef.current = requestDiffStat;
    requestDiffStat();
  }, [requestDiffStat]);

  const value = useMemo(() => {
    return {
      isLoadingDiff,
      isLoadingDiffStat,
      loadDiff,
      loadFileDiff,
      loadDiffStat,
      diffMetadataProps,
    };
  }, [
    isLoadingDiff,
    isLoadingDiffStat,
    loadDiff,
    loadFileDiff,
    loadDiffStat,
    diffMetadataProps,
  ]);

  return (
    <DiffContext.Provider value={value}>{props.children}</DiffContext.Provider>
  );
};
